import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Wallet, Clock, CheckCircle, XCircle, 
  Loader2, DollarSign, Calendar, TrendingUp, AlertCircle,
  ChevronRight, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayoutHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, completed, rejected

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all challenges
      const challengesRes = await axios.get(`${API_URL}/api/challenges/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenges(challengesRes.data);

      // Fetch all withdrawals for each funded challenge
      const fundedChallenges = challengesRes.data.filter(c => c.status === 'funded');
      const allWithdrawals = [];
      
      for (const challenge of fundedChallenges) {
        try {
          const withdrawalsRes = await axios.get(`${API_URL}/api/withdrawals/${challenge.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          allWithdrawals.push(...withdrawalsRes.data.map(w => ({
            ...w,
            challenge_name: challenge.package_name,
            challenge_id: challenge.id
          })));
        } catch (err) {
          // Challenge might not have withdrawals endpoint
        }
      }
      
      // Sort by date descending
      allWithdrawals.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setWithdrawals(allWithdrawals);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">{status}</Badge>;
    }
  };

  const fundedAccounts = challenges.filter(c => c.status === 'funded');
  const totalWithdrawn = withdrawals
    .filter(w => w.status === 'completed' || w.status === 'approved')
    .reduce((sum, w) => sum + (w.amount || 0), 0);
  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');

  const filteredWithdrawals = filter === 'all' 
    ? withdrawals 
    : withdrawals.filter(w => w.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="payout-history">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8"
        >
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="border-white/10 text-white hover:bg-white/5"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">
                Payout <span className="gradient-text">History</span>
              </h1>
              <p className="text-gray-500 text-sm">Track all your withdrawals and payouts</p>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8"
        >
          <Card className="bg-[hsl(220,20%,7%)] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Withdrawn</p>
                  <p className="font-mono text-2xl font-bold text-green-400 mt-1">
                    {formatCurrency(totalWithdrawn)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(220,20%,7%)] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Payouts</p>
                  <p className="font-mono text-2xl font-bold text-yellow-400 mt-1">
                    {pendingWithdrawals.length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(220,20%,7%)] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Funded Accounts</p>
                  <p className="font-mono text-2xl font-bold text-cyan-400 mt-1">
                    {fundedAccounts.length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-cyan-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Funded Accounts Quick Access */}
        {fundedAccounts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <h2 className="font-heading text-lg font-bold text-white mb-4">Your Funded Accounts</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {fundedAccounts.map((account) => (
                <Card 
                  key={account.id}
                  className="bg-[hsl(220,20%,7%)] border-white/10 hover:border-blue-500/30 cursor-pointer transition-all"
                  onClick={() => navigate(`/withdrawals/${account.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Wallet className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-heading font-bold text-white">{account.package_name}</p>
                          <p className="text-xs text-gray-500">{account.profit_split || 90}% profit split</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-mono font-bold ${account.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatCurrency(account.profit_loss)}
                        </p>
                        <ChevronRight className="w-4 h-4 text-gray-500 ml-auto" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* Withdrawal History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold text-white">Withdrawal History</h2>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-1.5 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {filteredWithdrawals.length === 0 ? (
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <Wallet className="w-8 h-8 text-gray-500" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2 text-white">No Withdrawals Yet</h3>
                <p className="text-gray-500 mb-6">
                  {fundedAccounts.length === 0 
                    ? "Get funded first to start withdrawing profits!"
                    : "You haven't made any withdrawal requests yet."}
                </p>
                {fundedAccounts.length === 0 ? (
                  <Button 
                    onClick={() => navigate('/challenges')}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  >
                    Get Funded
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate(`/withdrawals/${fundedAccounts[0].id}`)}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  >
                    Request Withdrawal
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredWithdrawals.map((withdrawal, index) => (
                <motion.div
                  key={withdrawal.id || index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-[hsl(220,20%,7%)] border-white/10 hover:border-white/20 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            withdrawal.status === 'completed' || withdrawal.status === 'approved'
                              ? 'bg-green-500/10'
                              : withdrawal.status === 'pending'
                              ? 'bg-yellow-500/10'
                              : 'bg-red-500/10'
                          }`}>
                            {withdrawal.status === 'completed' || withdrawal.status === 'approved' ? (
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : withdrawal.status === 'pending' ? (
                              <Clock className="w-5 h-5 text-yellow-400" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-heading font-bold text-white">
                              {formatCurrency(withdrawal.amount)}
                            </p>
                            <p className="text-xs text-gray-500 flex items-center gap-2">
                              <span>{withdrawal.challenge_name}</span>
                              <span>•</span>
                              <span>{formatDate(withdrawal.created_at)}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(withdrawal.status)}
                          {withdrawal.status === 'pending' && (
                            <span className="text-xs text-gray-500">Processing within 24h</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-blue-300 font-medium mb-1">Payout Information</p>
                  <ul className="text-blue-300/80 space-y-1">
                    <li>• First payout available after 6 trading days</li>
                    <li>• Subsequent payouts every 14 days</li>
                    <li>• All withdrawals processed within 24 hours</li>
                    <li>• KYC verification required for withdrawals</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PayoutHistory;
